import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import omit from 'lodash/omit';

import { moment } from '@eb/date';
import { gettext } from '@eventbrite/i18n';

import { InputField } from '@eb/eds-input-field';
import { STYLE_BASIC } from '@eb/eds-input-field';
import RangePickerController from './RangePickerController';

import { datePropType, getAdditionalProps } from '@eventbrite/eds-utils';
import { hasNativeDateControl, hasTouchEvents } from '@eventbrite/eds-utils';
import {
    ISO_8601_DATE_FORMAT,
    DEFAULT_INPUT_DISPLAY_FORMAT,
} from '@eventbrite/eds-utils';

import { COLOR_PROP_TYPE, START_DATE, END_DATE } from '../constants';
import { Icon } from '@eb/eds-icon';
import { CalendarChunky } from '@eb/eds-iconography';

const getInputprops = (extraAttrs) => {
    const extraInputProps = pick(extraAttrs, Object.keys(InputField.propTypes));
    const extraDatePickerControllerProps = omit(
        extraAttrs,
        Object.keys(InputField.propTypes),
    );

    return { extraInputProps, extraDatePickerControllerProps };
};

export const DateInputDefaultPrefix = () => (
    <Icon type={<CalendarChunky />} color="grey-800" />
);

const DateInput = ({
    shouldUsePicker,
    prefix = <DateInputDefaultPrefix />,
    ...inputProps
}) => {
    let type = 'text';

    if (!shouldUsePicker) {
        type = 'date';
    }

    return <InputField prefix={prefix} type={type} {...inputProps} />;
};

/* In the case that the client has touch events, we want to instead use the browser native experience */
const DateComponent = ({
    shouldUsePicker,
    focusedInput,
    startDate,
    endDate,
    onFocusChange,
    onChange,
    onClickOutside,
    ...datePickerProps
}) => {
    let component = null;

    if (shouldUsePicker) {
        component = (
            <RangePickerController
                {...datePickerProps}
                onFocusChange={onFocusChange}
                onDatesChange={onChange}
                onClickOutside={onClickOutside}
                focusedInput={focusedInput}
                startDate={startDate}
                endDate={endDate}
            />
        );
    }

    return component;
};

const _hasNativeMobilePicker = hasTouchEvents() && hasNativeDateControl();

const _formatInputValue = (value, { shouldUsePicker, inputDisplayFormat }) =>
    value &&
    moment(value).format(
        shouldUsePicker ? inputDisplayFormat : ISO_8601_DATE_FORMAT,
    );

export default class SingleDatePickerController extends PureComponent {
    static propTypes = {
        defaultValue: datePropType,
        value: datePropType,
        id: PropTypes.string,
        name: PropTypes.string,
        minDate: datePropType,
        maxDate: datePropType,
        startDate: datePropType,
        endDate: datePropType,
        hasError: PropTypes.bool,
        onChange: PropTypes.func,
        onBlur: PropTypes.func,
        onFocus: PropTypes.func,
        inputDisplayFormat: PropTypes.string,
        color: COLOR_PROP_TYPE,
        containerClasses: PropTypes.string,
        isEndInput: PropTypes.bool,
        readOnly: PropTypes.bool,
        shouldUsePicker: PropTypes.bool,
    };

    static defaultProps = {
        startDate: null,
        endDate: null,
        isEndInput: false,
        inputDisplayFormat: DEFAULT_INPUT_DISPLAY_FORMAT,
        shouldUsePicker: !_hasNativeMobilePicker,

        /* Input Defaults */
        id: 'date-picker-input',
        readOnly: false,
        /* eslint-disable react/default-props-match-prop-types */
        // this rule is disabled for these lines because of how the props are being passed downstream
        label: gettext('Date'),
        style: STYLE_BASIC,
        /* eslint-enable react/default-props-match-prop-types */
    };

    constructor(props) {
        super(props);

        const value = props.value || props.defaultValue;

        this.state = {
            value,
            isActive: false,
            inputValue: _formatInputValue(value, props),
        };
    }

    UNSAFE_componentWillReceiveProps({ value, ...restProps }) {
        if (value !== undefined && value !== this.state.value) {
            this.setState({
                value,
                inputValue: _formatInputValue(value, restProps),
            });
        }
    }

    _handleDatesChange = (dates) => {
        const { onChange, isEndInput } = this.props;
        const dateRangeType = isEndInput ? END_DATE : START_DATE;
        const date = dates[dateRangeType];

        this.setState(
            {
                value: date,
                inputValue: _formatInputValue(date, this.props),
            },
            () => {
                if (onChange) {
                    onChange(this.state.value);
                }
            },
        );
    };

    _handleInputFocus = () => {
        const { onFocus } = this.props;

        this.setState({ isActive: true }, () => {
            if (onFocus) {
                onFocus(this.state.value);
            }
        });
    };

    _handleInputChange = (inputValue) => {
        this.setState({
            inputValue,
        });
    };

    _handleInputBlur = () => {
        const { onChange, inputDisplayFormat, shouldUsePicker } = this.props;

        this.setState(
            ({ inputValue, value }) => {
                // return early if inputValue is empty to allow for users to clear input
                if (inputValue === '') {
                    return {
                        value: '',
                    };
                }

                const date = moment(
                    inputValue,
                    shouldUsePicker ? inputDisplayFormat : ISO_8601_DATE_FORMAT,
                    true,
                );

                if (date.isValid()) {
                    return {
                        value: date.format(ISO_8601_DATE_FORMAT),
                        inputValue: _formatInputValue(date, this.props),
                    };
                }

                // fall through case for any real value unparseable by moment
                // this includes values entered that do not match the `inputDisplayFormat`
                return {
                    inputValue: _formatInputValue(value, this.props),
                };
            },
            () => {
                if (onChange) {
                    onChange(this.state.value);
                }
            },
        );
    };

    _handleCloseAndBlurCalendar = () => {
        this.setState({ isActive: false }, () => {
            const { onBlur } = this.props;

            if (onBlur) {
                onBlur(this.state.value);
            }
        });
    };

    render() {
        const {
            startDate,
            endDate,
            containerClasses,
            minDate,
            maxDate,
            hasError,
            id,
            name,
            isEndInput,
            readOnly,
            shouldUsePicker,
        } = this.props;
        const { value, isActive, inputValue } = this.state;

        const dateRangeType = isEndInput ? END_DATE : START_DATE;
        const extraAttrs = getAdditionalProps(this);

        const {
            extraInputProps,
            extraDatePickerControllerProps,
        } = getInputprops(extraAttrs);

        return (
            <div className={containerClasses}>
                <DateInput
                    {...extraInputProps}
                    onFocus={this._handleInputFocus}
                    onChange={this._handleInputChange}
                    onBlur={this._handleInputBlur}
                    value={inputValue}
                    id={id}
                    name={name}
                    hasError={hasError}
                    shouldUsePicker={shouldUsePicker}
                    readOnly={readOnly}
                />
                <DateComponent
                    {...extraDatePickerControllerProps}
                    onFocusChange={this._handleCloseAndBlurCalendar}
                    onChange={this._handleDatesChange}
                    onClickOutside={this._handleCloseAndBlurCalendar}
                    focusedInput={isActive ? dateRangeType : null}
                    startDate={startDate || value}
                    endDate={endDate || value}
                    minDate={minDate}
                    maxDate={maxDate}
                    shouldUsePicker={shouldUsePicker}
                />
            </div>
        );
    }
}
